"use client";
import type { requestTravelAction } from "@/actions/requestTravel";
import { PhoneInput } from "@/components/PhoneInput";
import { PreventScroll } from "@/components/PreventScroll";
import { Close } from "@/components/newIcons/Close";
import { type UserInfoData, UserInfoSchema, setUserInfo } from "@/lib/userInfo";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { useAction } from "next-safe-action/hooks";
import type { PropsWithChildren } from "react";
import usePortal from "react-cool-portal";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Input } from "ui";
import { PrimaryButton } from "ui/components";

type Props = {
  travelId: number;
  versionId: number;
  departureId: number;
  travelTitle: string;
  onSuccess?: () => void;
  requestTravelAction: typeof requestTravelAction;
};
export const RequestTravel = ({
  travelId,
  travelTitle,
  versionId,
  departureId,
  onSuccess,
  children,
  requestTravelAction,
}: PropsWithChildren<Props>) => {
  const { Portal, hide, show } = usePortal({
    defaultShow: false,
  });
  const { control, handleSubmit, getValues } = useForm<UserInfoData>({
    resolver: valibotResolver(UserInfoSchema),
    defaultValues: {
      email: "",
      telephone: "",
    } satisfies UserInfoData,
  });
  const { execute, status } = useAction(requestTravelAction, {
    onSuccess: () => {
      const { email, telephone } = getValues();
      setUserInfo({
        email,
        telephone,
      });
      hide();
      if (onSuccess) onSuccess();
    },
  });

  const onSubmit: SubmitHandler<UserInfoData> = (data) => {
    execute({
      email: data.email,
      travelId,
      versionId,
      departureId,
      phone: data.telephone,
    });
  };

  return (
    <>
      {children && <div onClick={show}>{children}</div>}
      <Portal>
        <div onClick={hide} className="fixed inset-0 z-50 bg-primary/80 p-4 xl:pt-40">
          <PreventScroll />
          <form
            className="w-full max-w-xl mx-auto bg-white p-5 lg:p-8 border border-[#E5E5E5] rounded-[5px] shadow-sm text-primary flex flex-col space-y-5"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex justify-between items-center">
              <p className="text-lg lg:text-xl leading-6 font-semibold">Recevoir une alerte</p>
              <PrimaryButton size="icon" onClick={hide}>
                <Close className="w-4 h-4" />
              </PrimaryButton>
            </div>
            <p className="text-sm">
              Merci de saisir votre adresse e-mail ci-dessous pour recevoir une alerte dès que les informations sur le
              voyage
              <span className="font-semibold">{` ${travelTitle} `}</span>
              seront complétées.
            </p>
            <Input id="email" label="Votre adresse e-mail *" name="email" control={control} />
            <p className="text-sm">
              Si vous souhaitez également être rappelé lorsque les informations seront complétées, merci de de saisir
              votre numéro de téléphone (optionnel).
            </p>
            <PhoneInput id="phone" label="Votre numéro de téléphone" name="telephone" control={control} />
            <div className="flex justify-end">
              <PrimaryButton
                type="submit"
                disabled={status === "executing"}
                className="w-full lg:w-auto"
                direction="up"
              >
                Recevoir une alerte
              </PrimaryButton>
            </div>
          </form>
        </div>
      </Portal>
    </>
  );
};
